import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { DiceError, MAX_DICE } from '@realmery/realm-core';
import { settings } from '../settings.ts';
import { clearHistory, roll, savePresets, useDice, type RollRecord } from './diceStore.ts';

/**
 * The Dice Roller (docs/PARITY-STEP-2-PLAN.md section 3), the desktop's Tools > Dice Roller as
 * a panel beside the page: type any roll or click a die, keep rolls you make often, and see
 * every roll in one history, newest first, with each die shown (dropped dice struck through,
 * natural 20s and 1s marked).
 */

const QUICK_DICE = ['d4', 'd6', 'd8', 'd10', 'd12', 'd20', 'd100', 'dF'] as const;

const HELP = [
  'd20, 3d6, d% (d100), 4dF (Fate)',
  '4d6kh3 keep highest 3 · 2d20kl1 keep lowest · 4d6dl1 drop lowest',
  '3d6! exploding · 5d10!>=9 explode on 9+',
  '2d6r1 reroll 1s · 2d6ro<3 reroll once',
  '6d10>=7 count successes',
  '+ - * / and ( ) - division rounds down',
  'min(a, b) · max(a, b) · abs(a) · if(test, then, else)',
];

const time = (at: number) => new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

export function DiceRoller({ onClose }: { onClose: () => void }) {
  const { history, presets } = useDice();
  const [expression, setExpression] = useState('');
  const [count, setCount] = useState(() => settings()['new.diceCount']);
  const [error, setError] = useState('');
  const [naming, setNaming] = useState<string | null>(null);
  const [presetName, setPresetName] = useState('');
  const [back, setBack] = useState(-1);
  const input = useRef<HTMLInputElement>(null);
  const countId = useId();

  useEffect(() => {
    input.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function rollText(text: string, label = '') {
    try {
      roll(text, label);
      setError('');
      setBack(-1);
    } catch (e) {
      setError(e instanceof DiceError ? e.message : 'That roll could not be made.');
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    rollText(expression);
  }

  // Up and Down step through earlier rolls, as on the desktop.
  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    const typed = history.filter((r) => !r.label).map((r) => r.expression);
    if (event.key === 'ArrowUp' && back + 1 < typed.length) {
      event.preventDefault();
      setBack(back + 1);
      setExpression(typed[back + 1]!);
    } else if (event.key === 'ArrowDown' && back > 0) {
      event.preventDefault();
      setBack(back - 1);
      setExpression(typed[back - 1]!);
    }
  }

  return (
    <aside className="side-panel dice-roller" aria-label="Dice Roller">
      <div className="side-panel-head">
        <h2>Dice Roller</h2>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>

      <form className="dice-input" onSubmit={submit}>
        <input
          ref={input}
          aria-label="Roll"
          value={expression}
          maxLength={500}
          placeholder="1d20 + 5, 4d6kh3, 6d10>=7..."
          title="Type any roll and press Enter - Up and Down step through earlier ones"
          onChange={(e) => setExpression(e.currentTarget.value)}
          onKeyDown={onKeyDown}
        />
        <button type="submit" className="primary">
          Roll
        </button>
      </form>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}

      <div className="dice-quick" role="group" aria-label="Roll a die">
        <label htmlFor={countId} className="visually-hidden">
          How many dice the buttons roll
        </label>
        <input
          id={countId}
          type="number"
          min={1}
          max={MAX_DICE}
          value={count}
          title="How many dice the buttons roll"
          onChange={(e) => setCount(Math.min(MAX_DICE, Math.max(1, Math.round(Number(e.currentTarget.value)) || 1)))}
        />
        {QUICK_DICE.map((d) => (
          <button
            key={d}
            type="button"
            title={`Roll ${d === 'dF' ? 'Fate dice (-1, 0, or +1)' : d} - as many as the count beside them`}
            onClick={() => {
              setExpression(`${count}${d}`);
              rollText(`${count}${d}`);
            }}
          >
            {d}
          </button>
        ))}
      </div>

      <h3>Saved rolls</h3>
      <div className="dice-presets">
        {presets.map((preset, i) => (
          <span key={`${preset.name}-${i}`} className="dice-preset">
            <button type="button" title={preset.expression} onClick={() => rollText(preset.expression, preset.name)}>
              {preset.name}
            </button>
            <button type="button" className="quiet" aria-label={`Remove the saved roll ${preset.name}`} onClick={() => savePresets(presets.filter((_, j) => j !== i))}>
              ×
            </button>
          </span>
        ))}
        {naming === null ? (
          <button
            type="button"
            className="quiet"
            title="Keep the roll in the box above as a saved roll"
            disabled={expression.trim() === ''}
            onClick={() => {
              setPresetName('');
              setNaming(expression.trim());
            }}
          >
            + Save roll…
          </button>
        ) : (
          <form
            className="dice-name"
            aria-label="Name this roll"
            onSubmit={(e) => {
              e.preventDefault();
              if (presetName.trim()) savePresets([...presets, { name: presetName.trim(), expression: naming }]);
              setNaming(null);
            }}
          >
            <label>
              Name this roll ({naming})
              <input value={presetName} maxLength={100} autoFocus onChange={(e) => setPresetName(e.currentTarget.value)} />
            </label>
            <button type="submit" className="primary" disabled={presetName.trim() === ''}>
              Save
            </button>
            <button type="button" onClick={() => setNaming(null)}>
              Cancel
            </button>
          </form>
        )}
      </div>

      <details className="dice-help">
        <summary>How to write a roll</summary>
        <ul>
          {HELP.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </details>

      <div className="dice-history-head">
        <h3>History</h3>
        <button type="button" className="quiet" disabled={history.length === 0} onClick={clearHistory}>
          Clear
        </button>
      </div>
      {history.length === 0 ? (
        <p className="meta">Rolls from here and from character sheets appear here.</p>
      ) : (
        <ol className="dice-history" aria-live="polite">
          {history.map((record) => (
            <RollRow key={`${record.at}-${record.text}`} record={record} />
          ))}
        </ol>
      )}
    </aside>
  );
}

function RollRow({ record }: { record: RollRecord }) {
  const mark = record.naturalMax ? ' dice-natural-max' : record.naturalMin ? ' dice-natural-min' : '';
  return (
    <li>
      <div className="dice-row-top">
        <strong className={`dice-total${mark}`} title={record.naturalMax ? 'A natural maximum' : record.naturalMin ? 'A natural 1' : undefined}>
          {record.total}
        </strong>
        {record.successes && <span className="meta">{record.total === '1' ? 'success' : 'successes'}</span>}
        <span className="dice-label">{record.label || record.expression}</span>
        <span className="meta dice-time">{time(record.at)}</span>
      </div>
      <div className="dice-breakdown">{record.text}</div>
    </li>
  );
}
